import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageSquare, Send, ArrowLeft, Loader2, Shield, User, Check, CheckCheck } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import CollapsiblePanel from "./CollapsiblePanel";

function ConversationList({ conversations, onSelect, isDark, bg }) {
  if (conversations.length === 0) {
    return (
      <div className={`text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
        <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p className="text-sm">{bg ? "Няма съобщения" : "No messages"}</p>
      </div>
    );
  }
  return (
    <div className="space-y-1" data-testid="conversation-list">
      {conversations.map(c => {
        const other = c.other_user || {};
        const isAdmin = other.role === "admin";
        return (
          <button key={c.conversation_id} onClick={() => onSelect(c)}
            className={`w-full text-left p-3 rounded-xl transition-all flex items-center gap-3 ${
              isDark ? 'hover:bg-[hsl(223,47%,15%)]' : 'hover:bg-gray-50'
            }`} data-testid={`conversation-${c.conversation_id}`}>
            <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
              isAdmin ? 'bg-gradient-to-r from-[#0EA5E9] to-[#6366F1]' : isDark ? 'bg-[hsl(223,47%,18%)]' : 'bg-gray-100'
            }`}>
              {isAdmin ? <Shield className="w-4 h-4 text-white" /> : <User className="w-4 h-4 text-gray-400" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <span className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-[#1A1A2E]'}`}>
                  {other.name || other.email || "Unknown"}
                </span>
                {c.unread > 0 && (
                  <span className="w-5 h-5 rounded-full bg-[#EF4444] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                    {c.unread}
                  </span>
                )}
              </div>
              <p className={`text-[11px] truncate mt-0.5 ${c.unread > 0 ? 'font-medium' : ''} ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {c.last_message}
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function ChatView({ otherUserId, otherUser, onBack, isDark, bg, API }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const { user } = useAuth();
  const myId = user?._id || user?.id;

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/messages/conversation/${otherUserId}`);
      setMessages(data.messages || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [API, otherUserId]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);
  useEffect(() => {
    const iv = setInterval(fetchMessages, 8000);
    return () => clearInterval(iv);
  }, [fetchMessages]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages/send`, { recipient_id: otherUserId, text: text.trim() });
      setText("");
      await fetchMessages();
    } catch { toast.error(bg ? "Грешка при изпращане" : "Send failed"); }
    finally { setSending(false); }
  };

  const isAdmin = otherUser?.role === "admin";

  return (
    <div className="flex flex-col h-full" data-testid="chat-view">
      {/* Header */}
      <div className={`flex items-center gap-3 p-3 border-b ${isDark ? 'border-[hsl(223,47%,20%)]' : 'border-gray-100'}`}>
        <button onClick={onBack} className={isDark ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-gray-700'}>
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
          isAdmin ? 'bg-gradient-to-r from-[#0EA5E9] to-[#6366F1]' : isDark ? 'bg-[hsl(223,47%,18%)]' : 'bg-gray-100'
        }`}>
          {isAdmin ? <Shield className="w-3.5 h-3.5 text-white" /> : <User className="w-3.5 h-3.5 text-gray-400" />}
        </div>
        <div>
          <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`}>
            {otherUser?.name || otherUser?.email || "Unknown"}
          </p>
          {isAdmin && <p className="text-[10px] text-[#0EA5E9]">{bg ? "Администратор" : "Administrator"}</p>}
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-3" ref={scrollRef}>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
        ) : messages.length === 0 ? (
          <div className={`text-center py-8 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
            <p className="text-sm">{bg ? "Започнете разговора" : "Start the conversation"}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {messages.map((m, i) => {
              const isMine = m.sender_id === String(myId);
              return (
                <div key={m.created_at || `msg-${i}`} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-[13px] leading-relaxed ${
                    isMine
                      ? 'bg-gradient-to-r from-[#0EA5E9] to-[#6366F1] text-white rounded-br-sm'
                      : isDark ? 'bg-[hsl(223,47%,15%)] text-gray-200 rounded-bl-sm' : 'bg-gray-100 text-gray-700 rounded-bl-sm'
                  }`} data-testid={`message-${i}`}>
                    {m.subject && <p className="text-[10px] font-bold opacity-80 mb-0.5">{m.subject}</p>}
                    <p style={{ whiteSpace: "pre-wrap" }}>{m.text}</p>
                    <div className={`flex items-center justify-end gap-1 mt-0.5 ${isMine ? 'text-white/60' : isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                      <span className="text-[9px]">
                        {new Date(m.created_at).toLocaleTimeString(bg ? "bg-BG" : "en-US", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                      {isMine && (m.read ? <CheckCheck className="w-3 h-3" /> : <Check className="w-3 h-3" />)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>

      {/* Input */}
      <div className={`p-3 border-t ${isDark ? 'border-[hsl(223,47%,20%)]' : 'border-gray-100'}`}>
        <div className="flex gap-2">
          <Input value={text} onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={bg ? "Напишете съобщение..." : "Type a message..."}
            className={`flex-1 h-9 text-sm ${isDark ? 'bg-[hsl(223,47%,15%)] border-[hsl(223,47%,20%)] text-white' : ''}`}
            data-testid="message-input" />
          <Button size="sm" className="h-9 px-3 bg-gradient-to-r from-[#0EA5E9] to-[#6366F1] text-white"
            onClick={send} disabled={sending || !text.trim()} data-testid="send-message-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function InboxPanel() {
  const { API, user } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [conversations, setConversations] = useState([]);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [loading, setLoading] = useState(true);
  const myId = String(user?.id || user?._id || "");

  const fetchConversations = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/messages/conversations`);
      setConversations(data.conversations || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [API]);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);
  useEffect(() => {
    const iv = setInterval(fetchConversations, 15000);
    return () => clearInterval(iv);
  }, [fetchConversations]);

  const openChat = (convo) => {
    // Prefer other_user.id from backend (authoritative), fallback to participants diff
    const otherUserId = convo.other_user?.id
      || (convo.participants || []).find(p => p !== myId)
      || null;
    if (!otherUserId || otherUserId === myId) {
      toast.error(bg ? "Не може да се отвори разговорът" : "Cannot open conversation");
      return;
    }
    setSelectedConvo({ ...convo, otherUserId });
  };

  const unreadTotal = conversations.reduce((s, c) => s + (c.unread || 0), 0);
  const unreadBadge = unreadTotal > 0 ? (
    <span className="w-5 h-5 rounded-full bg-[#EF4444] text-white text-[10px] font-bold flex items-center justify-center" data-testid="inbox-unread-count">
      {unreadTotal}
    </span>
  ) : null;

  return (
    <CollapsiblePanel
      title={bg ? "Съобщения" : "Messages"}
      icon={MessageSquare}
      iconColor="#0EA5E9"
      credits={0}
      badge={unreadBadge}
      defaultOpen={false}
      storageKey="inbox-panel"
      testId="inbox-panel"
    >
      <div style={{ minHeight: selectedConvo ? 350 : 100 }}>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
        ) : selectedConvo ? (
          <ChatView
            otherUserId={selectedConvo.otherUserId}
            otherUser={selectedConvo.other_user}
            onBack={() => { setSelectedConvo(null); fetchConversations(); }}
            isDark={isDark} bg={bg} API={API}
          />
        ) : (
          <ConversationList conversations={conversations} onSelect={openChat} isDark={isDark} bg={bg} />
        )}
      </div>
    </CollapsiblePanel>
  );
}
